/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: Pricing
 * File     : Pricing.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the Pricing view and coordinating data fetching.
 *   Exports: Pricing
 *   
 *
 * Dependencies:
 *   - ../context/AuthContext
 *   - ../services/api
 *   - @mui/icons-material/Cancel
 *   - @mui/icons-material/CheckCircle
 *   - @mui/icons-material/CreditCard
 *   - @mui/icons-material/Email
 *   - @mui/icons-material/Lock
 *   - @mui/material
 *   - react
 *
 * Usage:
 *   Use in Router: <Route path="/pricing" element={<Pricing />} />
 * -----------------------------------------------------------------------------
 */
import React from 'react';
import { Box, Typography, Grid, Paper, List, ListItem, ListItemIcon, ListItemText, Button, Chip, Stack, Select, MenuItem } from '@mui/material';
import CheckIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Cancel';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Feature = ({ ok, children }) => (
  <ListItem dense>
    <ListItemIcon>
      {ok ? <CheckIcon color="success" fontSize="small" /> : <CloseIcon color="disabled" fontSize="small" />}
    </ListItemIcon>
    <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={children} />
  </ListItem>
);

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === 'owner';
  const isHostActive = isOwner || user?.planStatus === 'active';
  const isFreeCurrent = !!user && !isOwner && user?.planStatus !== 'active';
  const startCheckout = async () => {
    try {
      const r = await api.post('/billing/checkout');
      if (r?.data?.url) window.location.href = r.data.url;
    } catch (e) {
      alert(e?.response?.data?.error || 'Subscriptions are temporarily unavailable while we finish testing.');
    }
  };

  const [extraSlots, setExtraSlots] = React.useState(9); // default Tier 1

  React.useEffect(() => {
    if (!user) return;
    const tier = Number(user.clubSlotsTier || 0);
    if (tier === 0) setExtraSlots(9); // suggest Tier 1
    if (tier === 1) setExtraSlots(9);
    if (tier === 2) setExtraSlots(19);
    if (tier === 3) setExtraSlots(29);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.clubSlotsTier]);
  const startAddSlots = async () => {
    if (!(user && (isOwner || user.planStatus === 'active'))) {
      alert('You need an active Host subscription to add more club slots.');
      return;
    }
    try {
      // Map selection to tier 1/2/3
      const tier = extraSlots === 9 ? 1 : (extraSlots === 19 ? 2 : 3);
      const r = await api.post('/billing/add-slots', { quantity: tier });
      if (r?.data?.url) window.location.href = r.data.url;
    } catch (e) {
      alert(e?.response?.data?.error || 'Unable to start add-ons checkout.');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Pricing</Typography>
      <Typography variant="body1" sx={{ mb: 1, opacity: 0.9 }}>
        Choose the plan that fits your group. Payments are processed securely with Stripe. You can manage your plan and tier from your Profile.
      </Typography>
      {user && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Your status: {isOwner ? 'Owner' : (user.planStatus ? `Host (${user.planStatus})` : 'Free')}
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">Free</Typography>
              <Chip label="$0" color="default" size="small" />
            </Stack>
            <List>
              <Feature ok>Participate in public polls</Feature>
              <Feature ok>View results and history</Feature>
              <Feature ok>Rate winning movies (after runtime; half‑stars)</Feature>
              <Feature ok>Rating countdowns (Results & Opportunities)</Feature>
              <Feature ok>Read club discussions</Feature>
              <Feature ok>Global Metrics (read‑only)</Feature>
              <Feature ok>Discord login</Feature>
              <Feature ok={false}>Create Clubs</Feature>
              <Feature ok={false}>Create club‑only polls</Feature>
              <Feature ok={false}>Post in club discussions</Feature>
              <Feature ok={false}>Plex library integration</Feature>
              <Feature ok={false}>Where to Watch (Watchmode streaming)</Feature>
              <Feature ok={false}>Discord Alerts via club webhooks</Feature>
            </List>
            <Button disabled={isOwner || isFreeCurrent || isHostActive} fullWidth variant={(isOwner || isFreeCurrent || isHostActive) ? 'outlined' : 'contained'} onClick={() => { if (!user) navigate('/register'); }}>
              {isOwner ? 'Included' : (isFreeCurrent ? 'Current Plan' : 'Choose Free')}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%', border: '2px solid', borderColor: 'primary.main' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">Host</Typography>
              <Chip label="$2.50 / month" color="primary" size="small" icon={<CreditCardIcon fontSize="small" />} />
            </Stack>
            <List>
              <Feature ok>Everything in Free</Feature>
              <Feature ok>Create up to 5 Clubs (private spaces)</Feature>
              <Feature ok>Create club‑only polls</Feature>
              <Feature ok>Post in club discussions</Feature>
              <Feature ok>Owner/Admin tools for your Clubs</Feature>
              <Feature ok>View Owner’s Plex Library (Host uses the platform Owner’s Plex only; no personal Plex on Host)</Feature>
              <Feature ok>Random Poll Generator (count/genre/actor/director)</Feature>
              <Feature ok>Where to Watch (Watchmode streaming)</Feature>
              <Feature ok>Discord Alerts via club webhooks</Feature>
              <Feature ok>Global Metrics (enhanced: personal series, weekly roundups)</Feature>
              <Feature ok>Club Streamer (YouTube, Twitch TV, Kick streaming with chat & playlists)</Feature>
              <Feature ok>Advanced Spam Prevention (rate limiting, similarity detection, behavior monitoring)</Feature>
              <Feature ok>Member Management (add/remove members, role management, display names)</Feature>
              <Feature ok>Search Owner's Physical Media Collection (browse and search during poll creation)</Feature>
              <Feature ok>Email verification (TOTP) and secure login</Feature>
            </List>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Chip size="small" icon={<LockIcon fontSize="small" />} label="Stripe secure checkout" />
              <Chip size="small" label="Cancel anytime" />
            </Stack>
            <Button fullWidth variant="contained" onClick={startCheckout} disabled={isHostActive}>
              {isHostActive ? 'Current Plan' : 'Subscribe'}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">Extra Club Slots</Typography>
              <Chip label="$1–$3 / month (tiers)" color="secondary" size="small" />
            </Stack>
            <List>
              <ListItem>
                <ListItemText
                  primary="Need more room than the Host plan’s 5 clubs? Tiered add‑ons unlock extra capacity and are a fun way to support the project!"
                  secondary="Host is plenty for most users (up to 5 clubs). Add‑ons: Tier 1 (6–9), Tier 2 (10–19), Tier 3 (20–29). Requires active Host plan."
                />
              </ListItem>
            </List>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
              <Select size="small" value={extraSlots} onChange={(e) => setExtraSlots(Number(e.target.value))} sx={{ minWidth: 280 }}
                MenuProps={{ disableScrollLock: true }}
                renderValue={(val) => {
                  const currentTier = Number(user?.clubSlotsTier || 0);
                  const label = val === 9 ? 'Tier 1: 6–9' : (val === 19 ? 'Tier 2: 10–19' : 'Tier 3: 20–29');
                  const currentVal = currentTier === 1 ? 9 : currentTier === 2 ? 19 : currentTier === 3 ? 29 : null;
                  const suffix = currentVal !== null && val === currentVal ? ' (current)' : '';
                  return `${label}${suffix}`;
                }}
              >
                <MenuItem value={9}>Tier 1: 6–9 slots ($1/mo){Number(user?.clubSlotsTier||0)===1?' — current':''}</MenuItem>
                <MenuItem value={19}>Tier 2: 10–19 slots ($2/mo){Number(user?.clubSlotsTier||0)===2?' — current':''}</MenuItem>
                <MenuItem value={29}>Tier 3: 20–29 slots ($3/mo){Number(user?.clubSlotsTier||0)===3?' — current':''}</MenuItem>
              </Select>
              <Button fullWidth variant="contained" onClick={startAddSlots} disabled={!isHostActive}>Purchase</Button>
            </Stack>
            {!isHostActive && (
              <Typography variant="caption" color="text.secondary">Requires an active Host subscription.</Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">Self‑Hosted</Typography>
              <Chip label="Email for details" color="secondary" size="small" icon={<EmailIcon fontSize="small" />} />
            </Stack>
            <List>
              <Feature ok>Everything in Host</Feature>
              <Feature ok>Run on your own infrastructure</Feature>
              <Feature ok>Database ownership</Feature>
              <Feature ok>Custom branding & configuration</Feature>
              <Feature ok>Unlimited clubs (Owner only)</Feature>
              <Feature ok>Plex integration (A‑Z filters, library browsing, metadata). ReelConsensus does not play media—Plex is only used to list titles for polling and voting.</Feature>
            </List>
            <Button fullWidth variant="outlined" component="a" href="mailto:info@reelconsensus.com?subject=Self-Hosted%20Movie%20Voting">Email for Pricing</Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Platform Administration</Typography>
            <List>
              <Feature ok>Admin Panel (Admin/Owner)</Feature>
              <Feature ok>GDPR Wipe (Admin/Owner)</Feature>
              <Feature ok>Database export/import (Owner)</Feature>
              <Feature ok>User subscription management (Owner)</Feature>
              <Feature ok>Personal Media Collection (import from external sources, barcode scanning, collection search)</Feature>
              <Feature ok>Collection Migration Tool (seamless import with metadata & images)</Feature>
              <Feature ok>Personal Media Collection Management (Owner)</Feature>
            </List>
            <Typography variant="caption" color="text.secondary">Some features require Owner role. GDPR wipe deletes comments, anonymizes votes, and reassigns clubs/polls to Owner.</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            ReelConsensus does not host or stream films. We do not support piracy; Plex integration is for organizing and voting on titles you already own or have lawful access to.
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Pricing;
